import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  Button,
  TablePagination,
} from '@mui/material';
import { ResponsiveTable, Column } from '../../components/mobile/ResponsiveTable';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StoreIcon from '@mui/icons-material/Store';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import axiosInstance from '../../lib/axios';
import { TableToolbar, FilterOption, ColumnConfig } from '../../components/TableToolbar';
import type { PaginatedResponse } from '../../types';
import { Customer, CustomerType, getCustomerDisplayName } from '../../types/customer';
import { palette } from '../../theme/colors';

const fetchCustomers = async (page: number, pageSize: number, search?: string): Promise<PaginatedResponse<Customer>> => {
  const params: Record<string, any> = {
    page: page + 1,
    limit: pageSize,
  };

  if (search) {
    params.search = search;
  }

  const response = await axiosInstance.get('/customers', { params });

  return {
    data: Array.isArray(response.data) ? response.data : response.data.data || [],
    total: response.data.total || (Array.isArray(response.data) ? response.data.length : 0),
  };
};

const filterOptions: FilterOption[] = [
  {
    field: 'type',
    label: 'Customer Type',
    type: 'select',
    options: [
      { value: CustomerType.INDIVIDUAL, label: 'Individual' },
      { value: CustomerType.BUSINESS, label: 'Business' },
    ],
  },
  {
    field: 'deliveryPreference',
    label: 'Delivery Preference',
    type: 'select',
    options: [
      { value: 'delivery', label: 'Prefers Delivery' },
      { value: 'pickup', label: 'Prefers Pickup' },
    ],
  },
  {
    field: 'hasLocation',
    label: 'Has Location',
    type: 'select',
    options: [
      { value: 'yes', label: 'Has GPS Location' },
      { value: 'no', label: 'No GPS Location' },
    ],
  },
];

const getExportColumns = (): ColumnConfig[] => [
  {
    field: 'type',
    headerName: 'Type',
    valueFormatter: (value) => value === CustomerType.BUSINESS ? 'Business' : 'Individual',
  },
  {
    field: 'name',
    headerName: 'Name',
    valueFormatter: (_, row) => getCustomerDisplayName(row as Customer),
  },
  { field: 'email', headerName: 'Email' },
  { field: 'phone', headerName: 'Phone' },
  { field: 'city', headerName: 'City' },
  { field: 'state', headerName: 'State' },
  {
    field: 'totalOrders',
    headerName: 'Total Orders',
    valueFormatter: (value) => value || 0,
  },
  {
    field: 'preferDelivery',
    headerName: 'Prefers Delivery',
    valueFormatter: (value) => value ? 'Yes' : 'No',
  },
  {
    field: 'preferPickup',
    headerName: 'Prefers Pickup',
    valueFormatter: (value) => value ? 'Yes' : 'No',
  },
];

interface CustomerListProps {
  onCreateClick?: () => void;
  onCustomerClick?: (customer: Customer) => void;
}

export const CustomerList = ({ onCreateClick, onCustomerClick }: CustomerListProps) => {
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['customers', page, pageSize, searchValue],
    queryFn: () => fetchCustomers(page, pageSize, searchValue),
  });

  const filteredData = useMemo(() => {
    if (!data?.data) return [];

    return data.data.filter((customer: Customer) => {
      // Type filter
      if (activeFilters.type && customer.type !== activeFilters.type) {
        return false;
      }

      // Delivery preference filter
      if (activeFilters.deliveryPreference === 'delivery' && !customer.preferDelivery) {
        return false;
      }
      if (activeFilters.deliveryPreference === 'pickup' && !customer.preferPickup) {
        return false;
      }

      // Has location filter
      if (activeFilters.hasLocation === 'yes' && (!customer.latitude || !customer.longitude)) {
        return false;
      }
      if (activeFilters.hasLocation === 'no' && customer.latitude && customer.longitude) {
        return false;
      }

      return true;
    });
  }, [data?.data, activeFilters]);

  const handleFilterChange = (field: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setActiveFilters({});
  };

  const paginatedData = useMemo(() => {
    const startIndex = page * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, page, pageSize]);

  // Column definitions for ResponsiveTable
  const tableColumns: Column<Customer>[] = [
    {
      field: 'name',
      headerName: 'Customer',
      render: (value, row) => {
        const isBusinessCustomer = row.type === CustomerType.BUSINESS;
        const displayName = getCustomerDisplayName(row);
        const avatarInitial = isBusinessCustomer
          ? row.companyName?.[0]?.toUpperCase()
          : row.firstName?.[0]?.toUpperCase();

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                bgcolor: isBusinessCustomer ? palette.infoMuted : palette.primaryMuted,
                color: isBusinessCustomer ? palette.info : palette.primary,
                width: 40,
                height: 40,
              }}
            >
              {avatarInitial || (isBusinessCustomer ? <BusinessIcon /> : <PersonIcon />)}
            </Avatar>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={500}>
                  {displayName}
                </Typography>
                <Chip
                  icon={isBusinessCustomer ? <BusinessIcon sx={{ fontSize: 12 }} /> : <PersonIcon sx={{ fontSize: 12 }} />}
                  label={isBusinessCustomer ? 'Business' : 'Individual'}
                  size="small"
                  color={isBusinessCustomer ? 'info' : 'default'}
                  variant="outlined"
                  sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {row.email}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'phone',
      headerName: 'Phone',
      render: (value) => value || '-',
    },
    {
      field: 'location',
      headerName: 'Location',
      render: (value, row) => {
        const city = row.city;
        const state = row.state;
        const hasGps = row.latitude && row.longitude;

        if (!city && !state) {
          return (
            <Typography variant="caption" color="text.secondary">
              Not provided
            </Typography>
          );
        }

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {hasGps && (
              <LocationOnIcon sx={{ fontSize: 16, color: palette.success }} />
            )}
            <Typography variant="body2">
              {[city, state].filter(Boolean).join(', ')}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'preferences',
      headerName: 'Preferences',
      render: (value, row) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {row.preferDelivery && (
            <Chip
              icon={<LocalShippingIcon sx={{ fontSize: 14 }} />}
              label="Delivery"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 24 }}
            />
          )}
          {row.preferPickup && (
            <Chip
              icon={<StoreIcon sx={{ fontSize: 14 }} />}
              label="Pickup"
              size="small"
              color="secondary"
              variant="outlined"
              sx={{ height: 24 }}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'totalOrders',
      headerName: 'Orders',
      render: (value, row) => {
        const count = row.totalOrders || 0;
        return (
          <Chip
            label={count}
            size="small"
            color={count > 0 ? 'primary' : 'default'}
            variant={count > 0 ? 'filled' : 'outlined'}
          />
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Customer Since',
      render: (value) => {
        const date = new Date(value);
        return (
          <Typography variant="body2">
            {date.toLocaleDateString()}
          </Typography>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      render: (_, row) => (
        <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => navigate(`/app/customers/${row.id}`)}
              sx={{ color: palette.primary }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View History">
            <IconButton
              size="small"
              onClick={() => navigate(`/app/customers/${row.id}/history`)}
              sx={{ color: palette.info }}
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {onCustomerClick && (
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => onCustomerClick(row)}
                sx={{ color: palette.warning }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  const handleRowClick = (customer: Customer) => {
    // Row click navigates to customer detail page
    navigate(`/app/customers/${customer.id}`);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">Customers</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            View and manage customers who have placed orders with your organization
          </Typography>
        </Box>
        {onCreateClick && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreateClick}
            sx={{ borderRadius: 2 }}
          >
            Nouveau client
          </Button>
        )}
      </Box>

      <Card>
        <CardContent>
          <TableToolbar
            title="Customers"
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchPlaceholder="Search by name or email..."
            filters={filterOptions}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            data={filteredData}
            columns={getExportColumns()}
            exportFileName="customers"
          />

          <ResponsiveTable
            columns={tableColumns}
            rows={paginatedData}
            loading={isLoading}
            emptyMessage="No customers found"
            primaryField="name"
            getPrimaryText={(row) => getCustomerDisplayName(row as Customer) || 'Unknown Customer'}
            onRowClick={handleRowClick}
          />
          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </CardContent>
      </Card>
    </Box>
  );
};
